import { useEffect, useState, useRef } from 'react';
import LightGallery from 'lightgallery/react';
import 'lightgallery/css/lightgallery.css';
import 'lightgallery/css/lg-zoom.css';
import 'lightgallery/css/lg-thumbnail.css';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgZoom from 'lightgallery/plugins/zoom';
import { getProviderGallery } from '../../api';
import { getImage } from '../../api/images/images';
import '../ecommerce/store/Store.css';

interface EcomPhotosTableProps {
  providerId: number;
  isActive: boolean;
}

interface GalleryItem {
  imageId: string;
  thumbnailId: string;
  imageUrl?: string;
  thumbnailUrl?: string;
  rowSpan?: number;
  galleryIndex?: number;
}

const COLUMNS = 4;
const ROW_HEIGHT = 180;
const GAP = 15;

const hashString = (value: string): number => {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return hash;
};

const buildSpanSequence = (
  count: number,
  targetSum: number,
  firstSpan?: number
): number[] | null => {
  const result: number[] = [];

  const backtrack = (idx: number, sum: number, last: number): boolean => {
    if (idx === count) return sum === targetSum;

    const remaining = count - idx - 1;
    const minRemain = remaining;
    const maxRemain = remaining * 3;

    const options = [2, 1, 3];
    for (const option of options) {
      if (option === last) continue;
      const nextSum = sum + option;
      const left = targetSum - nextSum;
      if (left < minRemain || left > maxRemain) continue;
      result.push(option);
      if (backtrack(idx + 1, nextSum, option)) return true;
      result.pop();
    }

    return false;
  };

  if (firstSpan !== undefined) {
    if (count <= 0 || firstSpan < 1 || firstSpan > 3) return null;
    const remaining = count - 1;
    const left = targetSum - firstSpan;
    if (left < remaining || left > remaining * 3) return null;
    result.push(firstSpan);
    if (backtrack(1, firstSpan, firstSpan)) return result;
    return null;
  }

  return backtrack(0, 0, 0) ? result : null;
};

const buildColumnPlan = (totalImages: number): number[][] => {
  // Distribute image counts per column, close to equal.
  const base = Math.floor(totalImages / COLUMNS);
  const counts = Array(COLUMNS).fill(base);
  let rest = totalImages - base * COLUMNS;
  for (let i = 0; i < COLUMNS && rest > 0; i++, rest--) counts[i]++;

  // Use a common target height across all columns to avoid large final gaps.
  const minCommonTarget = Math.max(...counts);
  const maxCommonTarget = Math.min(...counts.map((count) => count * 3));
  if (minCommonTarget <= maxCommonTarget) {
    const preferredTarget = Math.max(
      minCommonTarget,
      Math.min(maxCommonTarget, Math.round((counts.reduce((a, b) => a + b, 0) * 2) / COLUMNS))
    );

    const targetCandidates = [
      preferredTarget,
      preferredTarget + 1,
      preferredTarget - 1,
      preferredTarget + 2,
      preferredTarget - 2,
    ].filter((value, idx, arr) => value >= minCommonTarget && value <= maxCommonTarget && arr.indexOf(value) === idx);

    const firstRowPatterns: number[][] = [
      [1, 2, 3, 2],
      [2, 1, 3, 1],
      [3, 1, 2, 1],
      [2, 3, 1, 2],
    ];
    const pattern = firstRowPatterns[totalImages % firstRowPatterns.length];

    for (const target of targetCandidates) {
      const sequences: Array<number[] | null> = [];
      for (let col = 0; col < COLUMNS; col++) {
        const count = counts[col];
        const firstSpan = count > 0 ? pattern[col] : undefined;
        const withFirst = firstSpan ? buildSpanSequence(count, target, firstSpan) : null;
        const sequence = withFirst ?? buildSpanSequence(count, target);
        sequences.push(sequence);
      }
      if (sequences.every(Boolean)) return sequences as number[][];
    }
  }

  // Fallback: simple equal counts with 1x1 to avoid broken layouts.
  const fallbackBase = Math.floor(totalImages / COLUMNS);
  const fallbackPlan = Array.from({ length: COLUMNS }, () => Array(fallbackBase).fill(1));
  let rem = totalImages - fallbackBase * COLUMNS;
  for (let i = 0; i < COLUMNS && rem > 0; i++, rem--) fallbackPlan[i].push(1);
  return fallbackPlan;
};

const buildColumns = (items: GalleryItem[]): { flat: GalleryItem[]; columns: GalleryItem[][] } => {
  const sortedItems = [...items].sort((a, b) => hashString(a.imageId) - hashString(b.imageId));
  const spanPlan = buildColumnPlan(sortedItems.length);
  const columns: GalleryItem[][] = Array.from({ length: COLUMNS }, () => []);

  let pointer = 0;
  for (let col = 0; col < COLUMNS; col++) {
    for (const span of spanPlan[col]) {
      if (pointer >= sortedItems.length) break;
      columns[col].push({ ...sortedItems[pointer], rowSpan: span, galleryIndex: pointer });
      pointer++;
    }
  }

  const flat = columns.flat();
  flat.forEach((item, idx) => {
    item.galleryIndex = idx;
  });

  return { flat, columns };
};

const EcomPhotosTable = ({ providerId, isActive }: EcomPhotosTableProps) => {
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  const [galleryColumns, setGalleryColumns] = useState<GalleryItem[][]>([]);
  const [loading, setLoading] = useState(true);
  const lgInstance = useRef<any>(null);
  const loadedProviderIdRef = useRef<number | null>(null);

  const onInit = (detail: any) => {
    if (detail) lgInstance.current = detail.instance;
  };

  const handleImageClick = (index: number) => {
    lgInstance.current?.openGallery(index);
  };

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        setLoading(true);
        const response = await getProviderGallery(providerId);

        const itemsWithUrls = await Promise.all(
          response.data.map(async (item: { imageId: string; thumbnailId: string }) => {
            try {
              const [imageUrl, thumbnailUrl] = await Promise.all([
                getImage(item.imageId),
                getImage(item.thumbnailId)
              ]);
              return { imageId: item.imageId, thumbnailId: item.thumbnailId, imageUrl, thumbnailUrl };
            } catch {
              return { imageId: item.imageId, thumbnailId: item.thumbnailId, imageUrl: undefined, thumbnailUrl: undefined };
            }
          })
        );

        const validItems = itemsWithUrls.filter(item => item.imageUrl && item.thumbnailUrl);
        const { flat, columns } = buildColumns(validItems);
        setGalleryItems(flat);
        setGalleryColumns(columns);
        loadedProviderIdRef.current = providerId;
      } catch (error) {
        console.error('Error fetching gallery:', error);
      } finally {
        setLoading(false);
      }
    };

    if (!providerId || !isActive) return;
    if (loadedProviderIdRef.current === providerId) return;
    fetchGallery();
  }, [providerId, isActive]);

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px' }}>
        <p>Cargando galería...</p>
      </div>
    );
  }

  if (galleryItems.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px' }}>
        <p style={{ fontWeight: 700 }}>No hay imágenes disponibles</p>
        <p>Este proveedor aún no ha subido fotos a su galería</p>
      </div>
    );
  }

  const dynamicEl = galleryItems.map(item => ({
    src: item.imageUrl ?? '',
    thumb: item.thumbnailUrl ?? '',
  }));

  return (
    <div className="bg-soft">
      {/* LightGallery en modo dinámico — solo maneja el lightbox, no el layout */}
      <LightGallery
        onInit={onInit}
        dynamic={true}
        dynamicEl={dynamicEl}
        speed={500}
        plugins={[lgThumbnail, lgZoom]}
        download={false}
      >
        <span />
      </LightGallery>

      {/* Layout visual completamente independiente */}
      <div className="gallery-masonry">
        {galleryColumns.map((col, colIdx) => (
          <div key={colIdx} className="gallery-column">
            {col.map((item) => {
              return (
                <div
                  key={`${item.imageId}-${item.galleryIndex}`}
                  className="gallery-item"
                  style={{ height: `${(item.rowSpan ?? 1) * ROW_HEIGHT + ((item.rowSpan ?? 1) - 1) * GAP}px` }}
                  onClick={() => handleImageClick(item.galleryIndex ?? 0)}
                >
                  <img src={item.thumbnailUrl} alt="" />
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default EcomPhotosTable;
